import React, { useState, useEffect, useRef } from 'react'
import { useNavigate, useLocation } from 'react-router-dom'
import { Button, Container, Row, Col, Spinner, Tooltip, OverlayTrigger } from 'react-bootstrap'
import Select from 'react-select'
import { recipeAPI } from '../api/recipeAPI'
import { brandAPI } from '../api/brandAPI'
import ResizableTable from '../components/ResizableTable'
import PaginationComponent from '../components/Pagination'
import { highlightText } from '../utils/commonUtils'
import { handleSearchWithDebounce } from '../utils/tableUtils'
import { usePermissions } from '../contexts/PermissionContext'
import SearchInputWithClear from '../components/SearchInputWithClear'
import ConfirmationModal from '../components/ConfirmationModal'
import StockItemInfo from '../components/StockItemInfo'
import CopyableText from '../components/CopyableText'
import '../styles/selectControl.css'

function RecipeListScreen() {
    const { hasPermission } = usePermissions()
    const navigate = useNavigate()
    const location = useLocation()
    const searchParams = new URLSearchParams(location.search)

    const [recipes, setRecipes] = useState([])
    const [loading, setLoading] = useState(true)
    const [searchTerm, setSearchTerm] = useState(searchParams.get('search') || '')
    const [searchInputValue, setSearchInputValue] = useState(searchParams.get('search') || '')
    const [currentPage, setCurrentPage] = useState(parseInt(searchParams.get('page')) || 1)
    const [totalPages, setTotalPages] = useState(0)
    const [pageSize, setPageSize] = useState(parseInt(searchParams.get('pageSize')) || PaginationComponent.getCurrentPageSize())
    const [showDeactivated, setShowDeactivated] = useState(searchParams.get('showDeactivated') === 'true')
    const [selectedBrand, setSelectedBrand] = useState(searchParams.get('brand') || '')
    const [brandMap, setBrandMap] = useState({})
    const debounceTimeoutRef = useRef(null)
    const [showDeleteModal, setShowDeleteModal] = useState(false)
    const [itemToDelete, setItemToDelete] = useState(null)

    const [columnWidths, setColumnWidths] = useState(() => {
        const savedWidths = localStorage.getItem('recipeColumnWidths');
        return savedWidths ? JSON.parse(savedWidths) : {
            recipe_id: 'auto',
            image: 70,
            gtin: 'auto',
            item_number: 'auto',
            name: 'auto',
            brand: 'auto',
            output_stock_item_name: 'auto',
            ingredients_count: 'auto',
            actions: 'auto'
        };
    });

    useEffect(() => {
        localStorage.setItem('recipeColumnWidths', JSON.stringify(columnWidths));
    }, [columnWidths]);

    useEffect(() => {
        async function fetchBrands() {
            try {
                const response = await brandAPI.getAll(1, 1000, '', false)
                const brands = response.results || []
                const mapping = brands.reduce((acc, brand) => {
                    acc[brand._id] = brand.name
                    return acc
                }, {})
                setBrandMap(mapping)
            } catch (error) {
                console.error('Error fetching brands:', error)
            }
        }
        fetchBrands()
    }, [])

    const updateUrlWithFilters = () => {
        const params = new URLSearchParams()
        if (searchTerm) params.set('search', searchTerm)
        if (selectedBrand) params.set('brand', selectedBrand)
        if (showDeactivated) params.set('showDeactivated', 'true')
        if (currentPage > 1) params.set('page', currentPage.toString())
        if (pageSize !== PaginationComponent.getCurrentPageSize()) params.set('pageSize', pageSize.toString())
        navigate(`${location.pathname}?${params.toString()}`, { replace: true })
    }

    useEffect(() => {
        updateUrlWithFilters()
    }, [searchTerm, selectedBrand, showDeactivated, currentPage, pageSize, location.pathname])

    useEffect(() => {
        async function fetchRecipes() {
            try {
                setLoading(true)
                const response = await recipeAPI.getAll(currentPage, pageSize, searchTerm, showDeactivated, selectedBrand || undefined)
                setRecipes(response.results)
                setTotalPages(response.count)
            } finally {
                setLoading(false)
            }
        }
        fetchRecipes()
    }, [currentPage, pageSize, searchTerm, showDeactivated, selectedBrand])

    const handleClearSearch = () => {
        setSearchInputValue('');
        setSearchTerm('');
        setCurrentPage(1);
        if (debounceTimeoutRef.current) {
            clearTimeout(debounceTimeoutRef.current);
        }
    };

    const handleSearchChange = (e) => {
        const value = e.target.value;
        setSearchInputValue(value);
        handleSearchWithDebounce(
            value,
            setSearchTerm,
            setCurrentPage,
            debounceTimeoutRef
        );
    };

    const formatBrandOptions = () => {
        return Object.entries(brandMap).map(([id, name]) => ({
            value: id,
            label: name
        }));
    };

    const handleBrandFilterChange = (selectedOption) => {
        setSelectedBrand(selectedOption ? selectedOption.value : '');
        setCurrentPage(1);
    };

    const handleDeleteClick = (e, recipe) => {
        e.stopPropagation();
        setItemToDelete(recipe);
        setShowDeleteModal(true);
    };

    const confirmDelete = async () => {
        if (itemToDelete) {
            try {
                await recipeAPI.delete(itemToDelete._id);
                setRecipes(recipes.filter(r => r._id !== itemToDelete._id));
            } catch (error) {
                console.error('Error deleting recipe:', error);
                alert(`Failed to delete recipe: ${error.response?.data?.message || error.message}`);
            } finally {
                setShowDeleteModal(false);
                setItemToDelete(null);
            }
        }
    };

    const handleToggleActive = async (e, recipe) => {
        e.stopPropagation();
        if (recipe.is_active) {
            if (!window.confirm('Are you sure you want to deactivate this recipe?')) {
                return;
            }
        }
        try {
            const updatedRecipe = { ...recipe, is_active: !recipe.is_active };
            await recipeAPI.update(recipe._id, updatedRecipe);
            setRecipes(recipes.map(r =>
                r._id === recipe._id ? updatedRecipe : r
            ));
        } catch (error) {
            console.error('Error toggling recipe status:', error);
            alert(`Failed to update recipe: ${error.response?.data?.error || error.response?.data?.message || error.message}`);
        }
    };

    return (
        <Container className="py-4">
            <Row className="mb-3 align-items-center">
                <Col>
                    <h1>Recipes</h1>
                </Col>
                <Col md={4}>
                    <SearchInputWithClear
                        value={searchInputValue}
                        onChange={handleSearchChange}
                        onClear={handleClearSearch}
                        placeholder="Search by ID, name, item #, GTIN..."
                        autoComplete="off"
                        spellCheck="false"
                    />
                </Col>
                <Col xs="auto">
                    <Button
                        variant={showDeactivated ? "secondary" : "outline-secondary"}
                        className="me-2"
                        onClick={() => {
                            setShowDeactivated(!showDeactivated);
                            setCurrentPage(1);
                        }}
                    >
                        {showDeactivated ? "Hide Deactivated" : "Show Deactivated"}
                    </Button>
                    {hasPermission('recipes.modify') && (
                        <Button
                            onClick={() => navigate('/recipe/create')}
                            variant="success"
                        >
                            Create Recipe
                        </Button>
                    )}
                </Col>
            </Row>

            {loading ? (
                <div className="text-center py-4">
                    <Spinner animation="border" role="status">
                        <span className="visually-hidden">Loading...</span>
                    </Spinner>
                </div>
            ) : (
                <>
                    <ResizableTable
                        tableId="recipes_table"
                        columns={[
                            {
                                id: 'recipe_id',
                                header: 'ID',
                                cell: (row) => highlightText(String(row._id), searchTerm)
                            },
                            {
                                id: 'image',
                                header: 'Image',
                                cell: (row) => row.output_stock_item_data?.image ? (
                                    <OverlayTrigger
                                        placement="right"
                                        overlay={
                                            <Tooltip className="stock-item-tooltip">
                                                <StockItemInfo stockItem={row.output_stock_item_data} />
                                            </Tooltip>
                                        }
                                    >
                                        <img
                                            src={row.output_stock_item_data.image}
                                            alt="Output Product"
                                            style={{
                                                width: '50px',
                                                height: '50px',
                                                objectFit: 'contain'
                                            }}
                                        />
                                    </OverlayTrigger>
                                ) : null
                            },
                            {
                                id: 'gtin',
                                header: 'GTIN',
                                cell: (row) => <CopyableText value={row.output_stock_item_data?.gtin} toastLabel="GTIN" searchTerm={searchTerm} />
                            },
                            {
                                id: 'item_number',
                                header: 'Item #',
                                cell: (row) => row.item_number ? <CopyableText value={row.item_number} toastLabel="Item Number" searchTerm={searchTerm} /> : ''
                            },
                            {
                                id: 'brand',
                                header: () => (
                                    <div style={{ minWidth: '200px' }} className="compact-select">
                                        <Select
                                            value={selectedBrand ? { value: selectedBrand, label: brandMap[selectedBrand] } : null}
                                            onChange={handleBrandFilterChange}
                                            options={formatBrandOptions()}
                                            isSearchable
                                            isClearable
                                            placeholder="Brand"
                                            className="react-select-container"
                                            classNamePrefix="react-select"
                                        />
                                    </div>
                                ),
                                cell: (row) => row.output_stock_item_data?.brand_name || ''
                            },
                            {
                                id: 'output_stock_item_name',
                                header: 'Output Product',
                                cell: (row) => (
                                    <>
                                        {highlightText(row.output_stock_item_data?.description || '-', searchTerm)}
                                        {!row.is_active && (
                                            <span className="ms-2 text-muted fst-italic">
                                                (Inactive)
                                            </span>
                                        )}
                                    </>
                                )
                            },
                            {
                                id: 'ingredients_count',
                                header: 'Ingredients',
                                cell: (row) => row.ingredients_count
                            },
                            {
                                id: 'actions',
                                header: 'Actions',
                                cell: (row) => (
                                    <div style={{ display: 'flex', gap: '4px', whiteSpace: 'nowrap' }}>
                                        {hasPermission('production.manage') && row.is_active && (
                                            <Button
                                                variant="primary"
                                                size="sm"
                                                onClick={(e) => {
                                                    e.stopPropagation()
                                                    const currentFilters = `?${new URLSearchParams({
                                                        search: searchTerm,
                                                        brand: selectedBrand,
                                                        showDeactivated: showDeactivated.toString(),
                                                        page: currentPage.toString(),
                                                        pageSize: pageSize.toString()
                                                    }).toString()}`
                                                    navigate(`/recipe/${row._id}/execute`, { state: { returnFilters: currentFilters } })
                                                }}
                                            >
                                                Execute
                                            </Button>
                                        )}
                                        {hasPermission('recipes.modify') && (
                                            <Button
                                                variant="danger"
                                                size="sm"
                                                onClick={(e) => handleDeleteClick(e, row)}
                                            >
                                                Delete
                                            </Button>
                                        )}
                                        {hasPermission('recipes.modify') && (
                                            <Button
                                                variant={row.is_active ? "danger" : "success"}
                                                size="sm"
                                                onClick={(e) => {
                                                    e.stopPropagation();
                                                    handleToggleActive(e, row);
                                                }}
                                            >
                                                {row.is_active ? 'Deactivate' : 'Activate'}
                                            </Button>
                                        )}
                                    </div>
                                )
                            }
                        ]}
                        data={recipes}
                        initialColumnWidths={columnWidths}
                        onRowClick={(row) => {
                            const currentFilters = `?${new URLSearchParams({
                                search: searchTerm,
                                brand: selectedBrand,
                                showDeactivated: showDeactivated.toString(),
                                page: currentPage.toString(),
                                pageSize: pageSize.toString()
                            }).toString()}`
                            navigate(`/recipe/${row._id}`, { state: { returnFilters: currentFilters } })
                        }}
                        striped
                        hover
                    />
                    <PaginationComponent
                        currentPage={currentPage}
                        totalItems={totalPages}
                        onPageChange={setCurrentPage}
                        onItemsPerPageChange={setPageSize}
                    />
                </>
            )}

            <ConfirmationModal
                show={showDeleteModal}
                onHide={() => {
                    setShowDeleteModal(false);
                    setItemToDelete(null);
                }}
                onConfirm={confirmDelete}
                title="Confirm Deletion"
                message={`Are you sure you want to delete the recipe "${itemToDelete?.name}"? All ingredients will also be deleted.`}
            />
        </Container>
    )
}

export default RecipeListScreen
